import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { getAuthSession, getValidAuthSession } from "@/lib/supabaseAuth";
import { Check, CreditCard, LockKeyhole, X } from "lucide-react";

const proFeatures = [
  "Todo lo del plan gratis",
  "Prospectos interesados",
  "Analisis de zona para presentar mejor tu propiedad",
  "Asesor IA para mostrar si tu propiedad es compatible con el giro del interesado",
];

const loadStripeScript = () =>
  new Promise((resolve, reject) => {
    if (window.Stripe) {
      resolve(window.Stripe);
      return;
    }

    const existingScript = document.querySelector("script[src='https://js.stripe.com/v3/']");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.Stripe));
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    script.onload = () => resolve(window.Stripe);
    script.onerror = reject;
    document.body.appendChild(script);
  });

const PlanProCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authSession, setAuthSession] = useState(() => getAuthSession());
  const cardMountRef = useRef(null);
  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const isPrivateTest = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("test") === "pro";
  }, [location.search]);
  const closeTarget =
    location.state?.returnTo || (authSession?.access_token ? "/mi-perfil" : "/");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [cardReady, setCardReady] = useState(false);

  const pricing = useMemo(
    () => ({
      subtotal: 99,
      iva: 0,
      total: 99,
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const setupCard = async () => {
      if (!isPrivateTest) return;

      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

      if (!publishableKey) {
        setPaymentError("Falta configurar la llave publica de Stripe.");
        return;
      }

      try {
        const Stripe = await loadStripeScript();
        if (cancelled || !cardMountRef.current || cardRef.current) return;

        const stripe = Stripe(publishableKey);
        const elements = stripe.elements({ locale: "es" });
        const card = elements.create("card", {
          hidePostalCode: false,
          style: {
            base: {
              color: "#0f172a",
              fontFamily: "Inter, system-ui, sans-serif",
              fontSize: "16px",
              "::placeholder": {
                color: "#94a3b8",
              },
            },
            invalid: {
              color: "#dc2626",
            },
          },
        });

        card.mount(cardMountRef.current);
        card.on("change", (event) => {
          setPaymentError(event.error?.message || "");
        });
        card.on("ready", () => setCardReady(true));

        stripeRef.current = stripe;
        cardRef.current = card;
      } catch (error) {
        if (!cancelled) {
          setPaymentError("No se pudo cargar el formulario seguro de tarjeta.");
        }
      }
    };

    setupCard();

    return () => {
      cancelled = true;
      if (cardRef.current) {
        cardRef.current.destroy();
        cardRef.current = null;
      }
      stripeRef.current = null;
    };
  }, [isPrivateTest]);

  useEffect(() => {
    let isMounted = true;

    getValidAuthSession().then((session) => {
      if (isMounted) setAuthSession(session);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPaymentError("");

    const currentSession = await getValidAuthSession();
    if (currentSession?.access_token) {
      setAuthSession(currentSession);
    }

    if (!currentSession?.access_token) {
      navigate(
        `/publicar-gratis?plan=pro&next=${encodeURIComponent("/plan-pro?test=pro")}`
      );
      return;
    }

    if (!stripeRef.current || !cardRef.current) {
      setPaymentError("El formulario de tarjeta todavia esta cargando.");
      return;
    }

    setIsProcessing(true);

    try {
      const response = await fetch("/api/create-pro-payment-intent", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (!response.ok || !data.clientSecret) {
        throw new Error(data.detail || data.error || "No se pudo iniciar el pago.");
      }

      const result = await stripeRef.current.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: cardRef.current,
          billing_details: {
            email: currentSession.user?.email || undefined,
            name:
              currentSession.user?.user_metadata?.nombre ||
              currentSession.user?.user_metadata?.name ||
              undefined,
          },
        },
      });

      if (result.error) {
        throw new Error(result.error.message || "No se pudo confirmar el pago.");
      }

      if (result.paymentIntent?.status === "succeeded") {
        navigate(
          `/registro?plan=pro&payment=success&payment_intent=${encodeURIComponent(
            result.paymentIntent.id
          )}`
        );
        return;
      }

      throw new Error("El pago no quedo confirmado. Intenta nuevamente.");
    } catch (error) {
      setPaymentError(error.message);
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-slate-50 px-4 py-24">
        {!isPrivateTest ? (
          <section className="mx-auto max-w-xl rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <h1 className="text-2xl font-black text-slate-950">
              Plan Pro en prueba
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Esta pagina todavia no esta disponible publicamente.
            </p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="mt-5 rounded-lg bg-[#F97316] px-5 py-3 text-sm font-black text-white"
            >
              Volver al inicio
            </button>
          </section>
        ) : (
          <section className="relative mx-auto grid max-w-6xl gap-6 lg:grid-cols-[0.95fr_1.05fr]">
            <button
              type="button"
              onClick={() => navigate(closeTarget)}
              aria-label="Salir"
              className="absolute right-0 top-[-52px] flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
            >
              <X size={18} />
            </button>
            <aside className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-[#F97316]">
                Plan seleccionado
              </p>
              <h1 className="mt-2 text-3xl font-black text-slate-950">
                Plan Pro
              </h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Activa analisis de zona, asesor IA y obten prospectos interesados.
              </p>

              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-slate-600">
                      Pago unico por publicacion
                    </p>
                    <p className="mt-2 inline-flex rounded-md bg-[#F97316] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-sm">
                      PROMOCION
                    </p>
                    <p className="mt-1 text-3xl font-black text-slate-950">
                      ${pricing.total.toLocaleString("es-MX")} MXN
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-500">
                      1 publicacion - 1 mes
                    </p>
                  </div>
                  <span className="rounded-md bg-[#F97316] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-sm">
                    PROMOCION
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                {proFeatures.map((feature) => (
                  <div key={feature} className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[#0056D2]">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span className="text-sm leading-5 text-slate-700">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </aside>

            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#0056D2]">
                  <CreditCard size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-950">
                    Pago con tarjeta
                  </h2>
                  <p className="text-sm text-slate-600">
                    Agrega cualquier tarjeta de debito o credito.
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-slate-200">
                <div className="flex justify-between border-b border-slate-100 px-4 py-3 text-sm">
                  <span className="text-slate-600">Plan Pro</span>
                  <span className="font-bold text-slate-950">
                    ${pricing.subtotal.toLocaleString("es-MX")} MXN
                  </span>
                </div>
                <div className="flex justify-between border-b border-slate-100 px-4 py-3 text-sm">
                  <span className="text-slate-600">IVA</span>
                  <span className="font-bold text-slate-950">
                    ${pricing.iva.toLocaleString("es-MX")} MXN
                  </span>
                </div>
                <div className="flex justify-between px-4 py-3 text-base">
                  <span className="font-black text-slate-950">Total</span>
                  <span className="font-black text-slate-950">
                    ${pricing.total.toLocaleString("es-MX")} MXN
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-black text-slate-950">
                    Selecciona tu metodo de pago
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Tarjeta de debito/credito
                  </p>
                  <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
                    <p className="mb-3 text-xs font-black uppercase tracking-wide text-slate-500">
                      Datos de la tarjeta
                    </p>
                    <div ref={cardMountRef} className="min-h-[44px]" />
                  </div>
                  <p className="mt-3 text-xs font-semibold text-slate-500">
                    Agrega cualquier tarjeta de debito o credito. Tulocalmx no guarda
                    los datos de tu tarjeta.
                  </p>
                </div>

                <div className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50 p-4 text-blue-900">
                  <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0" />
                  <p className="text-sm leading-6">
                    El pago se confirma de forma segura sin guardar los datos de
                    tu tarjeta en Tulocalmx.
                  </p>
                </div>

                {paymentError && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
                    {paymentError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isProcessing || !cardReady}
                  className="w-full rounded-lg bg-[#F97316] p-3 font-black text-white transition hover:bg-orange-600 disabled:bg-orange-300"
                >
                  {isProcessing
                    ? "Procesando pago..."
                    : cardReady
                    ? "Agregar tarjeta y pagar"
                    : "Cargando pago seguro..."}
                </button>
              </form>
            </section>
          </section>
        )}
      </main>
      <FloatingWhatsApp />
    </>
  );
};

export default PlanProCheckout;
